// @flow
import type { Action, Client, GetState } from 'state/types';
import {
  ADMIN_LOGIN,
  ADMIN_LOGIN_FAIL,
  ADMIN_LOGIN_SUCCESS,
  ADMIN_LOGOUT,
  ADMIN_LOGOUT_FAIL,
  ADMIN_LOGOUT_SUCCESS,
  ADMIN_PROPERTIES_INTERVAL_RESOURCE,
  CLEAR_IDENTITY_SESSION,
  GET_PUBLIC_KEY,
  GET_PUBLIC_KEY_SUCCESS,
  GET_PUBLIC_KEY_FAIL,
  IDENTITY_LOGIN,
  IDENTITY_LOGIN_FAIL,
  IDENTITY_LOGIN_SUCCESS,
  IDENTITY_LOGOUT,
  IDENTITY_LOGOUT_FAIL,
  IDENTITY_LOGOUT_SUCCESS,
  RESOURCE_ADMIN_PROPERTIES_INTERVAL,
  SET_LAST_ACTIVITY,
} from 'state/types/authTypes';
import type { Auth, PollingTimeoutHandler } from 'state/types/authTypes';
import { API } from 'util/constants';

const DEFAULT_CLIENT_INACTIVITY_TIMEOUT: number = 1000 * 60 * 15;

// In electron, for testing, we support an environment variable that will override
// the production inactivity timeout of 1 hour.
export function getClientInactivityTimeout() {
  if (window.CLIENT_INACTIVITY_TIMEOUT) {
    return window.CLIENT_INACTIVITY_TIMEOUT * 1000;
  }
  if (process.env.CLIENT_INACTIVITY_TIMEOUT) {
    return Number(process.env.CLIENT_INACTIVITY_TIMEOUT) * 1000;
  }
  return DEFAULT_CLIENT_INACTIVITY_TIMEOUT;
}

const initialState: Auth = {
  adminSessionId: null,
  identitySessionId: null,
  username: null,
  lastActivity: Date.now(),
  identityEncryptedPassword: null,
  publicKeyInfo: null,
};

export default function reducer(
  state: Auth = initialState,
  action: Action
): Auth {
  switch (action.type) {
    case ADMIN_LOGIN:
      return {
        ...state,
        username: action.payload.username,
      };
    case ADMIN_LOGIN_SUCCESS:
      return {
        ...state,
        adminSessionId: action.payload.session,
      };
    case ADMIN_LOGIN_FAIL:
      return {
        ...state,
        adminSessionId: null,
      };
    case ADMIN_LOGOUT_SUCCESS:
    case ADMIN_LOGOUT_FAIL:
      return {
        ...state,
        adminSessionId: null,
        username: null,
      };
    case CLEAR_IDENTITY_SESSION:
      return {
        ...state,
        identitySessionId: null,
      };
    case IDENTITY_LOGIN:
      return {
        ...state,
        username: action.payload.username,
        identityEncryptedPassword: action.payload.encryptedPassword,
      };
    case IDENTITY_LOGIN_SUCCESS:
      return {
        ...state,
        identitySessionId: action.payload.session,
      };
    case IDENTITY_LOGIN_FAIL:
      return {
        ...state,
        identitySessionId: null,
      };
    case IDENTITY_LOGOUT_SUCCESS:
    case IDENTITY_LOGOUT_FAIL:
      return {
        ...state,
        identitySessionId: null,
      };
    case SET_LAST_ACTIVITY:
      return {
        ...state,
        lastActivity: action.payload.lastActivity,
      };
    default:
      return state;
  }
}

function setAdminPropertiesPollInterval(payload: PollingTimeoutHandler) {
  return {
    type: RESOURCE_ADMIN_PROPERTIES_INTERVAL,
    resource: ADMIN_PROPERTIES_INTERVAL_RESOURCE,
    payload,
  };
}

function adminLogin(username: string, encryptedPassword: string) {
  return {
    types: [ADMIN_LOGIN, ADMIN_LOGIN_SUCCESS, ADMIN_LOGIN_FAIL],
    payload: { username },
    promise: (client: Client, getState: GetState) => {
      return client.post(`${API}/auth/login`, {
        data: { username, encryptedPassword },
      });
    },
  };
}

function adminLogout(session: string) {
  return {
    types: [ADMIN_LOGOUT, ADMIN_LOGOUT_SUCCESS, ADMIN_LOGOUT_FAIL],
    promise: (client: Client, getState: GetState) => {
      return client.post(`${API}/auth/logout`, {
        params: { session },
      });
    },
  };
}

function clearIdentitySession() {
  return {
    type: CLEAR_IDENTITY_SESSION,
  };
}

function identityLogin(
  username: ?string,
  encryptedPassword: ?string,
  isRsaEncryption: ?boolean = false
) {
  return {
    types: [IDENTITY_LOGIN, IDENTITY_LOGIN_SUCCESS, IDENTITY_LOGIN_FAIL],
    payload: { username: username || '', encryptedPassword },
    promise: (client: Client, getState: GetState) => {
      return client.post(`${API}/auth/identity-login`, {
        data: { username, encryptedPassword, isRsaEncryption },
      });
    },
  };
}

function identityLogout(session: string) {
  return {
    types: [IDENTITY_LOGOUT, IDENTITY_LOGOUT_SUCCESS, IDENTITY_LOGOUT_FAIL],
    promise: (client: Client, getState: GetState) => {
      return client.post(`${API}/auth/identity-logout`, {
        params: { session },
      });
    },
  };
}

function updateLastActivity() {
  return {
    type: SET_LAST_ACTIVITY,
    payload: { lastActivity: Date.now() },
  };
}

function getPublicKeyInfo() {
  return {
    types: [GET_PUBLIC_KEY, GET_PUBLIC_KEY_SUCCESS, GET_PUBLIC_KEY_FAIL],
    promise: (client: Client) => client.get(`${API}/public-key`),
  };
}

const actions = {
  adminLogin,
  adminLogout,
  getPublicKeyInfo,
  identityLogin,
  identityLogout,
  clearIdentitySession,
  setAdminPropertiesPollInterval,
  updateLastActivity,
};

export { actions };



// WEBPACK FOOTER //
// ./src/state/reducers/authReducer.js